import { Injectable } from '@angular/core'
import posthog from 'posthog-js'

import { ENV_CONFIG } from '../../bin/env.config'

/**
 * Thin wrapper around posthog-js so pages and services depend on a typed
 * Angular service instead of touching the global `window.posthog`.
 *
 * Contract:
 *  - `init()` is idempotent and silently no-ops if `ENV_CONFIG.posthogKey` is
 *    missing (useful when a dev strips the key locally).
 *  - Every capture/identify/reset call is wrapped in try/catch: analytics
 *    failures must never break signup, login, or navigation.
 *  - Calls made before `init()` succeeds are silently dropped (no queueing —
 *    we init from `AppComponent` constructor before any route fires).
 *
 * Config MUST match the marketing site's posthog.init config so the
 * `distinct_id` cookie set on salonmonster.com is reused when a visitor
 * lands on #/register.
 */
@Injectable({ providedIn: 'root' })
export class PostHogService {
  private initialized = false

  public init (): void {
    if (this.initialized) {
      return
    }

    const key = ENV_CONFIG.posthogKey
    const host = ENV_CONFIG.posthogHost

    if (!key || !host) {
      console.warn('[PostHogService] posthogKey/posthogHost missing from ENV_CONFIG; skipping init')
      return
    }

    try {
      posthog.init(key, {
        api_host: host,
        // Marketing site uses `true` (multi-page static site). This is an SPA,
        // so we fire $pageview manually on NavigationEnd in AppComponent.
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: false,
        persistence: 'localStorage+cookie',
        // Admin surface with client/PII data — never record sessions/heatmaps.
        disable_session_recording: true
      })
      this.initialized = true
    } catch (err) {
      console.error('[PostHogService] init failed', err)
    }
  }

  public capture (event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.capture(event, properties)
    } catch (err) {
      console.error('[PostHogService] capture failed', event, err)
    }
  }

  public capturePageview (currentUrl: string, properties?: Record<string, unknown>): void {
    this.capture('$pageview', { $current_url: currentUrl, ...(properties || {}) })
  }

  public identify (distinctId: string | number, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.identify(String(distinctId), properties)
    } catch (err) {
      console.error('[PostHogService] identify failed', err)
    }
  }

  public reset (): void {
    if (!this.initialized) {
      return
    }
    try {
      posthog.reset()
    } catch (err) {
      console.error('[PostHogService] reset failed', err)
    }
  }

  public isInitialized (): boolean {
    return this.initialized
  }
}
